import { useContext, useEffect, useState } from "react";
import {
    VStack,
    HStack,
    Box,
    Text,
    Button,
    Icon,
    Switch,
    Badge,
    useToast,
} from "@chakra-ui/react";
import { MdDragHandle, MdFlashOn, MdFlashOff } from "react-icons/md";

import {
    DndContext,
    closestCenter,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
    arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AppContext } from "../../../context/AppContext";
import { getServers, updateServers } from "../../../utils/api";

const BRAND = "rgb(220, 20, 60)";
const BRAND_LIGHT = "rgb(229, 60, 94)";

/* ─────────────────────────────────────────
   SortableItem
────────────────────────────────────────── */
const SortableItem = ({ id, label, serverNumber, isEditMode, isDisabled, showSwitch, onToggle }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id, disabled: isDisabled });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <Box
            ref={setNodeRef}
            style={style}
            w="100%"
            bg={isDisabled ? "gray.50" : "white"}
            border="1px solid"
            borderColor={isDragging ? BRAND_LIGHT : isDisabled ? "gray.300" : "gray.200"}
            borderRadius="md"
            boxShadow={
                isDragging
                    ? "rgba(220, 20, 60, 0.25) 0px 8px 24px"
                    : "rgba(149, 157, 165, 0.2) 0px 8px 24px"
            }
            px={4}
            py={3}
            transition="box-shadow 0.2s, border-color 0.2s, background 0.2s"
        >
            <HStack spacing={3} justify="space-between">
                {/* Left: drag handle + label */}
                <HStack spacing={3}>
                    <Icon
                        as={MdDragHandle}
                        fontSize="22px"
                        color={
                            !isEditMode
                                ? "transparent"
                                : isDisabled
                                    ? "gray.200"
                                    : BRAND_LIGHT
                        }
                        cursor={
                            !isEditMode
                                ? "default"
                                : isDisabled
                                    ? "not-allowed"
                                    : "grab"
                        }
                        _active={{
                            cursor:
                                isEditMode && !isDisabled ? "grabbing" : "default",
                        }}
                        {...(isEditMode && !isDisabled
                            ? { ...attributes, ...listeners }
                            : {})}
                        aria-label="Drag handle"
                        flexShrink={0}
                    />
                    <Text
                        fontWeight="semibold"
                        color={isDisabled ? "gray.400" : "gray.800"}
                        fontSize="md"
                        textDecoration={isDisabled ? "line-through" : "none"}
                    >
                        {label}
                    </Text>
                    {/* Server position number */}
                    <Box
                        as="span"
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        bg={isDisabled ? "gray.200" : BRAND}
                        color="white"
                        borderRadius="full"
                        fontSize="0.7em"
                        fontWeight="bold"
                        minW="1.5rem"
                        h="1.5rem"
                        px={1}
                    >
                        #{serverNumber}
                    </Box>
                    {isDisabled && (
                        <Badge colorScheme="red" variant="subtle" fontSize="0.7em">
                            Disabled
                        </Badge>
                    )}
                </HStack>

                {/* Right: enable/disable switch (only on qualifying rows) */}
                {showSwitch && isEditMode && (
                    <HStack spacing={2}>
                        {/* ON icon – left */}
                        <Icon
                            as={MdFlashOn}
                            fontSize="20px"
                            color={!isDisabled ? "green.500" : "gray.300"}
                        />
                        <Switch
                            id={`switch-${id}`}
                            isChecked={isDisabled}
                            onChange={() => onToggle(id)}
                            onPointerDown={(e) => e.stopPropagation()}
                            colorScheme="red"
                            size="md"
                            sx={{
                                "span.chakra-switch__track:not([data-checked])": {
                                    bg: "green.500",
                                },
                            }}
                        />
                        {/* OFF icon – right */}
                        <Icon
                            as={MdFlashOff}
                            fontSize="20px"
                            color={isDisabled ? "red.400" : "gray.300"}
                        />
                    </HStack>
                )}
            </HStack>
        </Box>
    );
};

/* ─────────────────────────────────────────
   Initial server list
────────────────────────────────────────── */

/* ─────────────────────────────────────────
   Helper – which indices should show a switch?

   Rule: show switch on index i if ALL servers
   at indices i+1 … end are disabled.
   (The very last server always qualifies.)
────────────────────────────────────────── */
const computeSwitchVisibility = (list) => {
    const result = new Array(list.length).fill(false);
    // Walk from the end; track whether every server seen so far is disabled
    let allAfterDisabled = true;
    for (let i = list.length - 1; i >= 0; i--) {
        result[i] = allAfterDisabled; // all servers AFTER i are disabled (vacuously true for last)
        if (!list[i].isDisabled) allAfterDisabled = false; // this server is enabled → stop cascade
    }
    return result;
};

/* ─────────────────────────────────────────
   Server page
────────────────────────────────────────── */
const Server = () => {
    const [servers, setServers] = useState([]);
    const [isEditMode, setIsEditMode] = useState(false);
    const [tempServers, setTempServers] = useState([]);
    const { AdminToken } = useContext(AppContext);
    const toast = useToast();
    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: { distance: 5 },
        }),
        useSensor(TouchSensor, {
            activationConstraint: { delay: 250, tolerance: 5 },
        })
    );

    /* ── edit / save / cancel ── */
    const handleEdit = () => {
        setTempServers(servers.map((s) => ({ ...s })));
        setIsEditMode(true);
    };

    const handleSave = () => {
        setServers(tempServers.map((s) => ({ ...s })));
        console.log(tempServers, 'checking here');
        updateServers(AdminToken, {data:tempServers}).then((res) => {
            setIsEditMode(false);
            toast({
                title: "Servers updated successfully",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        }).catch((err) => {
            console.log(err, 'checking here');
        });
    };

    const handleCancel = () => {
        setTempServers(servers.map((s) => ({ ...s })));
        setIsEditMode(false);
    };

    /* ── drag ── */
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = tempServers.findIndex((s) => s._id === active.id);
            const newIndex = tempServers.findIndex((s) => s._id === over.id);
            // Reorder and reassign `server` position numbers (1-based)
            setTempServers((prev) =>
                arrayMove(prev, oldIndex, newIndex).map((s, i) => ({
                    ...s,
                    server: String(i + 1),
                }))
            );
        }
    };

    /* ── toggle disable (works in both view & edit mode) ── */
    const handleToggle = (id) => {
        const updater = (list) =>
            list.map((s) => (s._id === id ? { ...s, isDisabled: !s.isDisabled } : s));

        if (isEditMode) {
            setTempServers((prev) => updater(prev));
        } else {
            setServers((prev) => updater(prev));
        }
    };
    useEffect(() => {
        getServers(AdminToken).then((res) => {
            // Sort by `server` field (position) ascending
            const sorted = [...res.data.data].sort(
                (a, b) => Number(a.server) - Number(b.server)
            );
            setServers(sorted);
            setTempServers(sorted);
        }).catch((err) => {
            console.log(err, 'error saying');
        });
    }, [])
    const displayList = isEditMode ? tempServers : servers;
    const switchVisibility = computeSwitchVisibility(displayList);

    return (
        <VStack
            gap={"1rem"}
            bg={"white"}
            align={"end"}
            justify={"start"}
            height={"100%"}
            minH={"100vh"}
            w={"100%"}
            p={["0.8rem 0.5rem", "1rem 1rem", "1.5rem"]}
        >
            {/* ── Header ── */}
            <HStack w="100%" justify="space-between" align="center">
                <Text fontSize={["lg", "xl", "2xl"]} fontWeight="bold" color="gray.700">
                    Server List
                </Text>

                {!isEditMode ? (
                    <Button
                        _hover={{ bgColor: "white", color: BRAND }}
                        onClick={handleEdit}
                        color={"white"}
                        bgColor={BRAND}
                        border={`2.4px solid ${BRAND} !important`}
                    >
                        Edit
                    </Button>
                ) : (
                    <HStack spacing={2}>
                        <Button
                            _hover={{ bgColor: "white", color: BRAND }}
                            onClick={handleSave}
                            color={"white"}
                            bgColor={BRAND}
                            border={`2.4px solid ${BRAND} !important`}
                        >
                            Save
                        </Button>
                        <Button
                            color={BRAND}
                            bg={"white"}
                            border={`2.4px solid ${BRAND} !important`}
                            _hover={{ bgColor: BRAND, color: "white" }}
                            onClick={handleCancel}
                        >
                            Cancel
                        </Button>
                    </HStack>
                )}
            </HStack>

            {/* ── Sortable list ── */}
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={displayList.map((s) => s._id)}
                    strategy={verticalListSortingStrategy}
                >
                    <VStack w="100%" spacing={3} align="stretch">
                        {displayList.map((server, index) => (
                            <SortableItem
                                key={server._id}
                                id={server._id}
                                label={server.serverName}
                                serverNumber={server.server}
                                isEditMode={isEditMode}
                                isDisabled={server.isDisabled}
                                showSwitch={switchVisibility[index]}
                                onToggle={handleToggle}
                            />
                        ))}
                    </VStack>
                </SortableContext>
            </DndContext>
        </VStack>
    );
};

export default Server;
